import React, { useEffect, useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { fetchCustomFields, upsertCustomFields, EntityType, FieldType, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const ENTITY_OPTIONS: EntityType[] = ["Candidates", "Companies", "Jobs", "Matches", "Lists", "Tasks", "Workforce"];
const FIELD_TYPE_OPTIONS: FieldType[] = ["text", "number", "boolean", "dropdown"];

const emptyFields: CustomFieldsJSON = {
  Candidates: [],
  Companies: [],
  Jobs: [],
  Matches: [],
  Lists: [],
  Tasks: [],
  Workforce: [],
};

export default function CustomFieldsTab() {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.id;
  const [fields, setFields] = useState<CustomFieldsJSON>(emptyFields);
  const [originalFields, setOriginalFields] = useState<CustomFieldsJSON>(emptyFields);
  const [activeEntity, setActiveEntity] = useState<EntityType>("Candidates");
  const [name, setName] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);
  const [dropdownInput, setDropdownInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [editingOptions, setEditingOptions] = useState<{ entity: EntityType; index: number } | null>(null);
  const [editOptionsValue, setEditOptionsValue] = useState<string[]>([]);
  const [editOptionInput, setEditOptionInput] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{ entity: EntityType; idx: number } | null>(null);

  useEffect(() => {
    if (organizationId) {
      setFetching(true);
      fetchCustomFields(organizationId)
        .then((data) => {
          setFields(data);
          setOriginalFields(data);
        })
        .catch(() => toast({ title: t("admin.customFields.failedToLoad"), description: t("admin.customFields.failedToLoadDesc"), variant: "destructive" }))
        .finally(() => setFetching(false));
    }
  }, [organizationId]);

  const handleAddField = () => {
    if (!name.trim()) return;
    const newField: CustomField = { name: name.trim(), type };
    if (type === "dropdown") newField.options = dropdownOptions;
    setFields((prev) => ({
      ...prev,
      [activeEntity]: [...(prev[activeEntity] || []), newField],
    }));
    setName("");
    setType("text");
    setDropdownOptions([]);
    setDropdownInput("");
  };

  const handleRemoveField = (entity: EntityType, idx: number) => {
    setFields((prev) => ({
      ...prev,
      [entity]: (prev[entity] || []).filter((_, i) => i !== idx),
    }));
    setDeleteConfirm(null);
  };

  function fieldsEqual(a: CustomFieldsJSON, b: CustomFieldsJSON) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  const hasUnsavedChanges = !fieldsEqual(fields, originalFields);

  const handleSave = async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      await upsertCustomFields(organizationId, fields);
      setOriginalFields(fields);
      toast({
        title: "Custom fields saved!",
        description: "Your changes have been saved.",
        duration: 3000,
      });
    } catch (e) {
      toast({
        title: "Failed to save custom fields",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditOptions = (entity: EntityType, idx: number, options: string[] = []) => {
    setEditingOptions({ entity, index: idx });
    setEditOptionsValue(options);
    setEditOptionInput("");
  };

  const cancelEditOptions = () => {
    setEditingOptions(null);
    setEditOptionsValue([]);
    setEditOptionInput("");
  };

  const saveEditOptions = () => {
    if (!editingOptions) return;
    setFields(prev => {
      const updated = { ...prev };
      const arr = [...updated[editingOptions.entity]];
      arr[editingOptions.index] = {
        ...arr[editingOptions.index],
        options: editOptionsValue,
      };
      updated[editingOptions.entity] = arr;
      return updated;
    });
    cancelEditOptions();
  };

  if (fetching) return <div className="p-6">{t("admin.customFields.loading")}</div>;

  if (!organizationId) {
    return <div className="p-6 text-red-600">{t("admin.customFields.noOrgSelected")}</div>;
  }

  return (
    <div className="p-6">
      {hasUnsavedChanges && (
        <Alert variant="default" className="mb-4 flex items-center bg-yellow-50 border-yellow-300">
          <AlertTriangle className="h-5 w-5 mr-2 text-yellow-500" />
          <AlertDescription>{t("admin.customFields.unsavedChanges")}</AlertDescription>
        </Alert>
      )}
      <Card className="p-6 mb-8 w-full">
        <div className="flex items-center justify-between mb-4 w-full">
          <Tabs value={activeEntity} onValueChange={v => setActiveEntity(v as EntityType)}>
            <TabsList>
              {ENTITY_OPTIONS.map(opt => (
                <TabsTrigger key={opt} value={opt}>{t(`admin.customFields.entity.${opt}`)}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Button onClick={handleSave} disabled={loading} className="bg-blue-500 text-white ml-4">
            {loading ? t("admin.customFields.saving") : t("admin.customFields.save")}
          </Button>
        </div>
        <Tabs value={activeEntity} onValueChange={v => setActiveEntity(v as EntityType)}>
          {ENTITY_OPTIONS.map(ent => (
            <TabsContent key={ent} value={ent}>
              <div className="flex flex-row items-end gap-4 mb-4 w-full">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium mb-1">{t("admin.customFields.fieldName")}</label>
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder={t("admin.customFields.fieldNamePlaceholder")} />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-sm font-medium mb-1">{t("admin.customFields.fieldType")}</label>
                  <Select value={type} onValueChange={v => setType(v as FieldType)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("admin.customFields.typePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{t(`admin.customFields.type.${opt}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {type === "dropdown" && (
                  <div className="flex-1 min-w-[260px] flex flex-col">
                    <label className="block text-sm font-medium mb-1">{t("admin.customFields.dropdownOptions")}</label>
                    <div className="flex flex-row gap-2 items-end">
                      <Input
                        value={dropdownInput}
                        onChange={e => setDropdownInput(e.target.value)}
                        placeholder={t("admin.customFields.addOptionPlaceholder")}
                        onKeyDown={e => {
                          if (e.key === "Enter" && dropdownInput.trim()) {
                            setDropdownOptions([...dropdownOptions, dropdownInput.trim()]);
                            setDropdownInput("");
                            e.preventDefault();
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (dropdownInput.trim()) {
                            setDropdownOptions([...dropdownOptions, dropdownInput.trim()]);
                            setDropdownInput("");
                          }
                        }}
                        type="button"
                        size="icon"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {dropdownOptions.map((opt, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-200 px-2 py-0.5 rounded text-sm flex items-center"
                        >
                          {opt}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="ml-1 text-red-500 hover:bg-transparent"
                            onClick={() => setDropdownOptions(dropdownOptions.filter((_, i) => i !== idx))}
                            type="button"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-end">
                  <Button className="bg-green-600 text-white" onClick={handleAddField} type="button">
                    <Plus className="mr-2 h-4 w-4" /> {t("admin.customFields.addField")}
                  </Button>
                </div>
              </div>
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/3">{t("admin.customFields.fieldName")}</TableHead>
                    <TableHead className="w-1/6">{t("admin.customFields.type")}</TableHead>
                    <TableHead className="w-1/3">{t("admin.customFields.options")}</TableHead>
                    <TableHead className="w-1/6 text-right">{t("admin.customFields.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!fields[ent] || fields[ent].length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-gray-400 text-center">{t("admin.customFields.none")}</TableCell>
                    </TableRow>
                  )}
                  {fields[ent]?.map((f, i) => {
                    const isEditing = editingOptions && editingOptions.entity === ent && editingOptions.index === i;
                    return (
                      <TableRow key={i}>
                        <TableCell className="font-semibold">{f.name}</TableCell>
                        <TableCell>{t(`admin.customFields.type.${f.type}`)}</TableCell>
                        <TableCell>
                          {f.type === "dropdown" ? (
                            isEditing ? (
                              <div className="flex flex-col gap-2">
                                <div className="flex flex-wrap gap-1">
                                  {editOptionsValue.map((opt, idx) => (
                                    <span key={idx} className="bg-gray-200 px-2 py-0.5 rounded text-sm flex items-center">
                                      {opt}
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="ml-1 text-red-500 hover:bg-transparent"
                                        onClick={() => setEditOptionsValue(editOptionsValue.filter((_, j) => j !== idx))}
                                        type="button"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </span>
                                  ))}
                                </div>
                                <div className="flex gap-2 mt-1">
                                  <Input
                                    value={editOptionInput}
                                    onChange={e => setEditOptionInput(e.target.value)}
                                    placeholder={t("admin.customFields.addOptionPlaceholder")}
                                    onKeyDown={e => {
                                      if (e.key === "Enter" && editOptionInput.trim()) {
                                        setEditOptionsValue([...editOptionsValue, editOptionInput.trim()]);
                                        setEditOptionInput("");
                                        e.preventDefault();
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="secondary"
                                    onClick={() => {
                                      if (editOptionInput.trim()) {
                                        setEditOptionsValue([...editOptionsValue, editOptionInput.trim()]);
                                        setEditOptionInput("");
                                      }
                                    }}
                                    type="button"
                                    size="icon"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="flex gap-2 mt-2">
                                  <Button size="sm" className="bg-blue-500 text-white" onClick={saveEditOptions} type="button">{t("admin.customFields.ok")}</Button>
                                  <Button size="sm" variant="outline" onClick={cancelEditOptions} type="button">{t("admin.customFields.cancel")}</Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span>{f.options ? f.options.join(", ") : ""}</span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-blue-500 hover:bg-transparent"
                                  onClick={() => startEditOptions(ent, i, f.options || [])}
                                  type="button"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </div>
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:bg-transparent"
                                onClick={() => setDeleteConfirm({ entity: ent, idx: i })}
                                type="button"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("admin.customFields.deleteConfirmTitle")}</AlertDialogTitle>
                                <AlertDialogDescription>{t("admin.customFields.deleteConfirmDesc")}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setDeleteConfirm(null)}>{t("admin.customFields.cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRemoveField(ent, i)}>{t("admin.customFields.delete")}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </div>
  );
} 